import { useState } from 'react';

import { useApiFetchUserLocation } from '#/api/apiFetchUserLocation';
import type { ApiInvite } from '#/api/invite/types/ApiInvite';
import { signUp } from '#/api/userEnvironment/apiUserEnvironment';
import type { ApiUser } from '#/api/userEnvironment/types/ApiUserEnvironment';
import type { FormUsageContext } from '#/components/Form/Form';
import type { FormSubmitFunction } from '#/components/Form/FormContext';
import type { SignupFormValues } from '#/components/SignupForm/SignupForm';
import { SignupForm } from '#/components/SignupForm/SignupForm';
import { useUserEnvironmentActions } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { AuthApiTmpStorage } from '#/modules/auth/api/tmpStorage/types/AuthApiTmpStorage';
import type { AuthLoginPageLocationState } from '#/modules/auth/pages/login/AuthLoginPage';
import { authLoginRoute } from '#/modules/auth/routing/auth.routes';
import { useOrgSavedToast } from '#/modules/listing/orgs/hooks/useOrgSavedToast';
import type { MovementPageFormValues } from '#/pages/movement/movementPageUtils';
import type { WydPageFormValues } from '#/pages/wyd/wydPageUtils';
import { HttpError } from '#/utils/HttpError';
import { trackSignUp } from '#/utils/analytics/track/trackSignUp';
import { configureTrackJs } from '#/utils/configureTrackJs';
import { deleteCookie } from '#/utils/cookies';
import { getThirdPartyLoginUrl } from '#/utils/getThirdPartyLoginUrl';
import {
  searchLocationToUserLocation,
  userLocationToSearchLocation,
} from '#/utils/location';
import { resetRecaptcha } from '#/utils/recaptcha';
import { showModal } from '#/zustand-stores/modalStore';

type Props = {
  analyticsTitle: string;
  usageContext: FormUsageContext;
  redirectTo?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  tmpStorage?: AuthApiTmpStorage;
  invite?: ApiInvite | null;
  onSignup?: ((user: ApiUser) => void) | null;
  onLogin?: (user: ApiUser) => void | null;
  uncloseable?: boolean;
  movementPageFormValues?: MovementPageFormValues | null;
  wydPageFormValues?: WydPageFormValues | null;
};

export function SignupFormContainer({
  analyticsTitle,
  usageContext,
  redirectTo,
  firstName,
  lastName,
  email,
  tmpStorage,
  invite,
  onSignup,
  onLogin,
  uncloseable,
  movementPageFormValues,
  wydPageFormValues,
}: Props) {
  const { locale } = useI18n();
  const { setUserEnvironment } = useUserEnvironmentActions();
  const [userLocation] = useApiFetchUserLocation();
  const { showOrgSavedToast } = useOrgSavedToast();

  const interestFormValues = movementPageFormValues ?? wydPageFormValues;

  const initialValues = {
    firstName: interestFormValues?.firstName ?? firstName ?? '',
    lastName: interestFormValues?.lastName ?? lastName ?? '',
    email: interestFormValues?.email ?? invite?.email ?? email ?? '',
    password: '',
    location:
      interestFormValues?.location ||
      userLocationToSearchLocation(userLocation),
    gRecaptchaResponse: undefined,
  };

  const [userCameFromMembershipPage, setUserCameFromMembershipPage] = useState(
    Boolean(interestFormValues),
  );

  const showLoginModal = (forEmail: string) => {
    showModal('LOGIN', {
      redirectTo,
      tmpStorage,
      email: forEmail,
      uncloseable,
      onLogin,
    });
  };

  const getLoginUrlAndState = (forEmail: string | undefined) => ({
    to: authLoginRoute.with({}),
    state: {
      redirectTo,
      email: forEmail,
      inviteId: invite?.id,
      tmpStorage,
    } satisfies AuthLoginPageLocationState,
  });

  const getSocialLoginUrl = (provider: 'facebook' | 'google' | 'apple') =>
    getThirdPartyLoginUrl({
      locale,
      provider,
      redirectTo,
      inviteId: invite?.id,
      tmpStorage,
    });

  const onSubmit: FormSubmitFunction<SignupFormValues> = ({
    values,
    handleError,
    updateSubmitting,
  }) => {
    deleteCookie('userDismissedNotificationPreferencesDialog');

    signUp({
      firstName: values.firstName,
      lastName: values.lastName,
      email: values.email,
      password: values.password,
      gRecaptchaResponse: values.gRecaptchaResponse,
      ...searchLocationToUserLocation(values.location),
      inviteId: invite?.id,
      redirectTo,
    })
      .then((userEnvironment) => {
        trackSignUp(userEnvironment.user, 'Email Password');
        configureTrackJs(userEnvironment.user.id);
        setUserEnvironment(userEnvironment);

        if (
          tmpStorage?.type === 'savedListing' &&
          tmpStorage.savedListing.type === 'ORG'
        )
          showOrgSavedToast();

        const emailChangedDuringInviteSignup =
          invite && values.email !== invite.email;

        if (emailChangedDuringInviteSignup) {
          // Reset form submitting state so the button becomes enabled
          updateSubmitting(false);
          showModal('VERIFY_EMAIL', {
            uncloseable: true,
            onVerified: () => onSignup?.(userEnvironment.user),
          });
        } else {
          onSignup?.(userEnvironment.user);
        }
        return;
      })
      .catch((error) => {
        // Check if this is a VM email verification error
        if (error instanceof HttpError && error.responseJson) {
          const vmVerifyEmailError = error.responseJson.errors?.find(
            (e) => e.type === 'vmVerifyEmail',
          );

          if (vmVerifyEmailError) {
            // Show VM email verification modal instead of form error
            showModal('VM_EMAIL_VERIFICATION', {
              email: values.email,
            });
            return;
          }
        }

        resetRecaptcha();
        handleError(error, {
          recaptchaKey: values.recaptchaKey + 1,
          gRecaptchaResponse: undefined,
        });

        // Show complete form if submission fails, because something
        // may have gone wrong with one of the invisible fields
        setUserCameFromMembershipPage(false);
      });
  };

  return (
    <SignupForm
      analyticsTitle={analyticsTitle}
      usageContext={usageContext}
      initialValues={initialValues}
      invite={invite}
      showLoginModal={showLoginModal}
      getLoginUrlAndState={getLoginUrlAndState}
      getThirdPartyLoginUrl={getSocialLoginUrl}
      onSubmit={onSubmit}
      userCameFromMembershipPage={userCameFromMembershipPage}
    />
  );
}
